/**
 * What a form document declares: the shape of a field, the tree it sits in, and the flattening
 * that turns that tree into the list an engine can be built from.
 *
 * Declaration only. Nothing here reads untrusted input — that is `./parse.ts` — and nothing here
 * builds a form — that is `./compile.ts`.
 */
import { MDY_FIELD_KINDS } from "../field-kinds.js";
import { explainValueMismatch } from "../value-contracts.js";
import { warnDev } from "./guards.js";
/**
 * What a field of this kind holds when it holds nothing.
 *
 * One table, in the lowest layer, because "empty" is a property of the kind rather than of whoever
 * renders it: two adapters answering separately is how the same form validates differently in each.
 *
 * A kind whose empty value is a *usable* value cannot be required. `number` is `null` and not `0`
 * for exactly that reason — zero is a number the user may well mean, so a field defaulted to it is
 * one `required` can never fail. `slider` is the deliberate exception: a thumb is always somewhere,
 * so an untouched slider sits at its minimum and reads as filled.
 */
export function mdyEmptyValueFor(field) {
    if (field.initialValue !== undefined) {
        // An initial a kind cannot hold is refused where it is declared, rather than becoming the value
        // the form starts from. `parseDynamicForm` now says so about a document; this is the same
        // sentence for a tree written in code, which is a defect to report rather than input to survive.
        // Kept, it made a form that was invalid before anybody touched it — the field reporting "holds
        // string" about a value the user never entered and cannot see how to correct.
        const mismatch = MDY_FIELD_KINDS.includes(field.kind)
            ? explainValueMismatch(field.kind, field.initialValue)
            : null;
        if (mismatch !== null) {
            // Dropped rather than thrown, and named while it is dropped. A form is the thing a person is
            // looking at: refusing to build one over a declared initial takes the whole page away, while
            // keeping it made a form that was invalid before anybody touched it — reporting "holds string"
            // about a value the user never entered and cannot see how to correct. The kind's own empty
            // value is what the field would have started from had the document said nothing.
            warnDev(`Field "${field.name}" declares an initialValue its kind cannot hold (${mismatch}); ` +
                "the field starts empty instead.", undefined, "MDY_DYNAMIC_INVALID_FIELD");
            return emptyForKind(field);
        }
        return field.initialValue;
    }
    return emptyForKind(field);
}
/** What a field of this kind starts from when its document says nothing usable. */
function emptyForKind(field) {
    switch (field.kind) {
        case "number":
            return null;
        // Its own minimum, not zero: a slider bounded 10–20 that starts at 0 sits outside the range it
        // declares, and the first drag is the only thing that would ever bring it back in.
        case "slider":
            return field.min ?? 0;
        case "checkbox":
        case "toggle":
            return false;
        case "multiselect":
        case "file":
            return [];
        case "daterange":
            return { start: null, end: null };
        case "select":
        case "radio":
        case "segmented":
        case "datepicker":
        case "timepicker":
            return null;
        case "text":
        case "textarea":
        case "email":
        case "password":
        case "colors":
            return "";
        default:
            return assertNeverField(field);
    }
}
/** Exhaustiveness helper for kind switches. */
export function assertNeverField(field) {
    throw new Error(`[modyra] Unknown dynamic field kind: ${JSON.stringify(field)}`);
}
// ─── Runtime validation of network-borne configs ─────────────────────────────
/**
 * Every kind the dynamic renderer knows how to draw.
 *
 * The vocabulary itself is a leaf module: a kind is what a field is, not something a document
 * format owns. Re-exported here because a document names one.
 */
export const MDY_DYNAMIC_FIELD_KINDS = MDY_FIELD_KINDS;
